// 장비함(가방 창 탭)·대장간 강화(대장간 창 탭) DOM(D2a·D2b). 표시 값은 gearModel.ts(순수), 변경은 GameCommand로만 보낸다.
// RpgUi 창 안의 본문 영역에만 그린다(플레이 화면 위 팝업 없음). 값이 바뀔 때만 다시 그린다(RpgUi가 revision·version으로 판단).
// 천장 게이지·실효 확률·기본 확률·비용·하락 위험을 강화 버튼 바로 위에 공개한다(PRD 4.3 "확률·천장 게이지 화면 공개").
import { STONE_TIERS } from '../shared/content/economy';
import { BAG_CAP } from '../shared/content/loot';
import { NAMED_COLOR } from '../shared/content/items';
import type { GameCommand, GameView } from '../shared/types';
import type { GearResultView } from '../shared/gearView';
import { FONT, FONT_NUM, el } from './dom';
import { PITY_RULE, SalvageConfirm, setRows, gearModeNotice, protectRequested, bagCards, otherHeroWornRows, enhanceModel, equippedCards, gearCompare, gearMessage, gearTipText, logRows, protectShopText, SLOT_KO, type GearCard } from './gearModel';
import { assetUrl } from '../pwa/assetMap';
import type { EquipSlot } from '../shared/content/types';
import { makeRequestId, type TxMessage } from './rpgModel';
import { SLOT_ORDER } from '../shared/gearLedger';
import { DEFAULT_BODY_W, forgeSplit, scrollTileGrid } from './shopLayout';
import { gearThumb, matChip } from './shopUi';
import { EQUIP_BEST_EVENT, equipHints, equipMarks } from './equipFeedbackModel';
import { installEquipFxStyle } from './equipFeedback';
import { MasteryPanel } from './masteryPanel';

export const GEAR_TAB_SVG = '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M12 2 4 5v6c0 5 3.4 9.4 8 11 4.6-1.6 8-6 8-11V5zm0 3.2 5 1.9V11c0 3.6-2.2 6.9-5 8.3-2.8-1.4-5-4.7-5-8.3V7.1z"/></svg>';
export const ENHANCE_TAB_SVG = '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="m14.5 2 1.2 3.3L19 6.5l-3.3 1.2-1.2 3.3-1.2-3.3L10 6.5l3.3-1.2zM4 13l7-7 2 2-7 7zm-1 3 3 3-2 2H2v-2zm11.6-1.6 1.4-1.4 5 5-1.4 1.4z"/></svg>';

const STYLE_ID = 'dh-gear-style';
const CSS =
  '.dh-g{display:flex;flex-direction:column;gap:10px;font-family:' + FONT + ';color:#eef2ff}' +
  '.dh-g-h{font:900 15px ' + FONT + ';color:#ffd76a;letter-spacing:.5px;display:flex;justify-content:space-between;align-items:baseline}' +
  '.dh-g-h small{font:700 12px ' + FONT + ';color:#aeb9da}' +
  '.dh-g-slots{display:grid;grid-template-columns:repeat(3,1fr);gap:6px}' +
  '.dh-g-bag{display:grid;grid-template-columns:repeat(auto-fill,minmax(120px,1fr));gap:6px;max-height:210px;overflow:auto;padding-right:2px}' +
  '.dh-g-cell{position:relative;min-height:54px;text-align:left;border-radius:8px;padding:6px 8px;border:2px solid var(--gc,#556);background:linear-gradient(180deg,rgba(30,40,72,.92),rgba(14,19,38,.92));color:#fff;cursor:pointer;font:800 12px ' + FONT + ';line-height:1.25}' +
  '.dh-g-cell.empty{border-style:dashed;border-color:rgba(174,185,218,.35);color:#aeb9da;cursor:default}' +
  '.dh-g-cell.sel{box-shadow:0 0 0 2px #fff3c0,0 0 14px var(--gc)}' +
  '.dh-g-cell b{display:block;color:var(--gc);font:900 11px ' + FONT + '}' +
  '.dh-g-cell i{font-style:normal;color:#aeb9da;font:700 11px ' + FONT_NUM + '}' +
  '.dh-g-card{border-radius:10px;border:2px solid var(--gc,#556);background:rgba(10,14,30,.85);padding:10px 12px;display:flex;flex-direction:column;gap:4px}' +
  '.dh-g-card .nm{font:900 17px ' + FONT + ';color:var(--gc)}' +
  '.dh-g-card .sub{font:700 12px ' + FONT + ';color:#aeb9da}' +
  '.dh-g-card .st{font:800 13px ' + FONT + '}' +
  '.dh-g-card .af{font:700 12px ' + FONT + ';color:#9fd0ff}' +
  '.dh-g-row{display:flex;gap:8px;flex-wrap:wrap;align-items:center}' +
  '.dh-g-btn{min-height:var(--dh-btn-min,44px);padding:6px 14px;border-radius:8px;border:2px solid #c8963e;background:linear-gradient(180deg,#2a3560,#151c38);color:#fff;font:900 14px ' + FONT + ';cursor:pointer}' +
  '.dh-g-btn.pri{background:linear-gradient(180deg,#f2c14e,#c8963e);color:#1a1206;border-color:#ffe7a0}' +
  '.dh-g-btn:disabled{opacity:.45;cursor:not-allowed}' +
  '.dh-g-mats{font:700 12px ' + FONT + ';font-variant-numeric:tabular-nums;color:#cfe0ff}' +
  '.dh-g-chance{font:900 30px ' + FONT_NUM + ';color:#fff;text-shadow:0 2px 6px rgba(0,0,0,.7)}' +
  '.dh-g-gauge{position:relative;height:12px;border-radius:6px;background:rgba(255,255,255,.08);border:1px solid rgba(242,193,78,.5);overflow:hidden}' +
  '.dh-g-gauge>span{position:absolute;left:0;top:0;bottom:0;background:linear-gradient(90deg,#f2c14e,#ff8a3a)}' +
  '.dh-g-note{font:700 11.5px ' + FONT + ';color:#aeb9da;line-height:1.35}' +
  '.dh-g-warn{font:800 12px ' + FONT + ';color:#ffb37a}' +
  '.dh-g-log{display:flex;flex-direction:column;gap:2px;font:700 12px ' + FONT + '}' +
  '.dh-g-split{display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1.25fr);gap:12px}' +
  '.narrow .dh-g-split{grid-template-columns:1fr}' +
  '.dh-g-list{display:flex;flex-direction:column;gap:5px;max-height:330px;overflow:auto}' +
  // 장비 아이콘 칸(ECONOMY 14절 장비 통합): 등급색 테두리·배경, 강화 단계·장착 표시
  '.dh-g-tile{position:relative;aspect-ratio:1;min-width:0;border-radius:10px;border:2px solid var(--gc,#556);padding:0;cursor:pointer;' +
  'background:radial-gradient(circle at 50% 38%,color-mix(in srgb,var(--gc,#556) 34%,#1b2440) 0%,#121a33 62%,#0b1022 100%);box-shadow:inset 0 0 12px rgba(0,0,0,.55);overflow:hidden}' +
  '.dh-g-tile img{position:absolute;inset:7%;width:86%;height:86%;object-fit:contain;filter:drop-shadow(0 2px 3px rgba(0,0,0,.6));pointer-events:none}' +
  // 세트·전설 배지(오른쪽 아래), 전설은 금빛 이중 테두리·은은한 빛(콘셉트 14-rarity-frames)
  '.dh-g-tile img.badge{inset:auto 2px 2px auto;width:34%;height:34%;filter:drop-shadow(0 1px 2px rgba(0,0,0,.8))}' +
  '.dh-g-tile.legend{border-color:#f2b233;box-shadow:0 0 0 1px #ffe7a0,0 0 10px rgba(242,178,51,.55),inset 0 0 14px rgba(255,170,40,.35)}' +
  '.dh-g-tile.set{box-shadow:0 0 8px rgba(63,208,122,.35),inset 0 0 12px rgba(0,0,0,.55)}' +
  '.dh-g-tile.empty{border-style:dashed;border-color:rgba(174,185,218,.35);background:rgba(14,19,38,.6);cursor:default}' +
  '.dh-g-tile.empty span{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;font:800 11px ' + FONT + ';color:#7d86a8;text-align:center}' +
  '.dh-g-tile.sel{box-shadow:0 0 0 2px #fff3c0,0 0 14px var(--gc),inset 0 0 12px rgba(0,0,0,.55)}' +
  '.dh-g-tile .enh{position:absolute;right:3px;top:2px;font:900 12px ' + FONT_NUM + ';color:#fff;text-shadow:0 1px 2px #000,0 0 4px #000}' +
  '.dh-g-tile .eq{position:absolute;left:3px;top:3px;padding:0 4px;border-radius:5px;background:#f2c14e;color:#1a1206;font:900 10px ' + FONT + ';line-height:15px}' +
  '.dh-g-tile .ban{position:absolute;inset:0;background:rgba(40,8,12,.45)}' +
  // 가방 격자: .dh-r-body(flex 세로, min-height:0)가 격자를 줄이면(flex:none 없이) 자동 행이 min-content=0(칸이 overflow:hidden이라 aspect-ratio 최소 크기 0)까지 눌려 윗줄과 겹친다.
  // flex:none으로 줄어들지 않게 하고, 칸 안 ::before가 폭만큼의 높이를 내용으로 줘 행 최소 높이를 칸 폭과 같게 둔다
  '.dh-g-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(62px,1fr));grid-auto-rows:auto;align-content:start;gap:6px;max-height:228px;overflow:auto;padding:2px;flex:none}' +
  '.dh-g-grid .dh-g-tile::before{content:"";display:block;padding-top:100%}' +
  '.dh-g-filter{display:flex;gap:5px;flex-wrap:wrap}' +
  '.dh-g-chip{min-height:32px;padding:2px 10px;border-radius:16px;border:1.5px solid rgba(242,193,78,.45);background:rgba(20,28,56,.85);color:#cfd8f5;font:800 12px ' + FONT + ';cursor:pointer}' +
  '.dh-g-chip[aria-pressed=true]{background:linear-gradient(180deg,#f2c14e,#c8963e);color:#1a1206;border-color:#ffe7a0}' +
  '.dh-g-detail{display:grid;grid-template-columns:76px minmax(0,1fr) minmax(0,.9fr);gap:10px;align-items:start}' +
  '.narrow .dh-g-detail{grid-template-columns:64px minmax(0,1fr)}' +
  '.narrow .dh-g-cmp{grid-column:1/-1}' +
  '.dh-g-cmp{border-left:1px solid rgba(242,193,78,.25);padding-left:10px;display:flex;flex-direction:column;gap:3px;font:700 12px ' + FONT + ';color:#aeb9da}' +
  '.narrow .dh-g-cmp{border-left:0;padding-left:0;border-top:1px solid rgba(242,193,78,.25);padding-top:6px}' +
  '.dh-g-cmp div{display:flex;gap:6px;justify-content:space-between;color:#eef2ff;font:800 12.5px ' + FONT + '}' +
  '.dh-g-cmp .up{color:#7dffa0}.dh-g-cmp .dn{color:#ff7a86}';

/**
 * 장비 그림 한 장: 전용 그림(드롭 등급별·세트)을 못 읽으면 대체 후보를 차례로 쓴다(그림이 아직 없어도 칸이 비지 않는다).
 * data-want에 처음 요청한 경로, data-icon에 실제로 보인 경로를 남긴다(QA가 읽는다)
 */
export function gearImg(parent: HTMLElement, path: string, fallbacks: string | readonly string[]): HTMLImageElement {
  const im = document.createElement('img');
  im.alt = ''; im.draggable = false; im.decoding = 'async';
  im.dataset.icon = path;
  im.dataset.want = path;
  const rest = (typeof fallbacks === 'string' ? [fallbacks] : fallbacks).filter((f) => f && f !== path);
  let i = 0;
  if (rest.length) {
    im.addEventListener('error', () => {
      if (i >= rest.length) return;
      const next = rest[i++];
      im.dataset.icon = next;
      im.src = assetUrl(next);
    });
  }
  im.src = assetUrl(path);
  parent.appendChild(im);
  return im;
}
/** 등급 배지(세트·전설): 칸 오른쪽 아래 모서리 작은 그림. 못 읽으면 숨긴다 */
export function badgeImg(parent: HTMLElement, path: string): HTMLImageElement {
  const bd = document.createElement('img');
  bd.alt = ''; bd.draggable = false; bd.decoding = 'async';
  bd.className = 'badge';
  bd.dataset.testid = 'gear-badge';
  bd.addEventListener('error', () => { bd.style.display = 'none'; });
  bd.src = assetUrl(path);
  parent.appendChild(bd);
  return bd;
}

const TONE: Record<string, string> = { ok: '#7dffa0', warn: '#ffe7a0', err: '#ff7a86' };

export class GearPanel {
  /** 내부 선택이 바뀌면 오른다(RpgUi 재그리기 키) */
  version = 0;
  private sel: string | null = null;
  /** 가방 부위 거르기 */
  private filter: 'all' | EquipSlot = 'all';
  private protect = false;
  private lastSeq = -1;
  /** 손상 복구 두 단계 확인(첫 버튼 → 확인 버튼). confirm()은 일부 웹뷰에서 막혀 있어 화면 안에서 묻는다 */
  private confirmRecover = false;
  /** 세트·전설 정리 두 단계 확인(c015) */
  private readonly salvage = new SalvageConfirm();
  /** 본문 안쪽 폭(설계 px, RpgUi가 그릴 때마다 넣는다)과 세로 화면 여부 — 강화 탭 격자·상세 배치(shopLayout) 입력 */
  bodyW = DEFAULT_BODY_W;
  narrow = false;
  /** 숙련 탭(캐릭터 창)과 대장간 초월 구역이 같이 쓰는 편집 상태(C4) */
  readonly masteryPanel: MasteryPanel;

  constructor(private readonly send: (c: GameCommand) => void) {
    this.masteryPanel = new MasteryPanel(send);
    installEquipFxStyle();
    if (typeof document !== 'undefined' && !document.getElementById(STYLE_ID)) {
      const st = document.createElement('style');
      st.id = STYLE_ID;
      st.textContent = CSS;
      document.head.appendChild(st);
    }
  }

  /** 창이 열릴 때 선택을 비운다 */
  open(): void { this.sel = null; this.protect = false; this.confirmRecover = false; this.salvage.cancel(); this.version++; }

  /** 저장 상태 안내 + 손상 복구(두 단계) */
  private modeNotice(root: HTMLElement, view: GameView): void {
    const n = gearModeNotice(view.gear.mode);
    if (!n) return;
    const box = el('div', 'border:2px solid #ff7a86;border-radius:8px;padding:8px 10px;background:rgba(60,10,20,.6);', root);
    box.dataset.testid = 'gear-mode-notice';
    el('div', 'font:800 13px ' + FONT + ';color:#ffe7a0;', box, '⚠ ' + n.text);
    if (!n.recover) return;
    const row = el('div', '', box);
    row.className = 'dh-g-row';
    row.style.marginTop = '6px';
    if (!this.confirmRecover) this.btn(row, '장비 기록 새로 시작', () => { this.confirmRecover = true; this.version++; }, { testid: 'gear-recover' });
    else {
      el('span', 'font:800 13px ' + FONT + ';color:#ffe7a0;', row, '정말 새로 시작할까요? 지금 장비함은 비워집니다.');
      this.btn(row, '네, 새로 시작', () => { this.confirmRecover = false; this.version++; this.send({ type: 'gearRecover', requestId: makeRequestId() }); }, { primary: true, testid: 'gear-recover-confirm' });
      this.btn(row, '취소', () => { this.confirmRecover = false; this.version++; }, { testid: 'gear-recover-cancel' });
    }
  }

  /** 새 결과(gear.last.seq)를 한 번만 문구로 알린다 */
  pollResult(last: GearResultView, show: (m: TxMessage) => void): void {
    if (this.lastSeq === -1) { this.lastSeq = last.seq; return; }
    if (last.seq === this.lastSeq) return;
    this.lastSeq = last.seq;
    if (last.code === 'duplicate') return;
    show(gearMessage(last));
  }

  private pick(uid: string): void { if (this.sel !== uid) { this.sel = uid; this.version++; } }

  private btn(parent: HTMLElement, label: string, onClick: () => void, o: { primary?: boolean; disabled?: boolean; testid?: string } = {}): HTMLButtonElement {
    const b = el('button', '', parent, label);
    b.type = 'button';
    b.className = 'dh-g-btn' + (o.primary ? ' pri' : '');
    b.dataset.sfx = 'event';
    if (o.testid) b.dataset.testid = o.testid;
    if (o.disabled) b.disabled = true;
    b.addEventListener('click', onClick);
    return b;
  }

  private mats(parent: HTMLElement, view: GameView): void {
    const g = view.gear;
    const line = el('div', '', parent);
    line.className = 'dh-g-mats';
    line.dataset.testid = 'gear-mats';
    line.textContent = '강화석 ' + STONE_TIERS.map((t) => t.name.replace(/강화석\((.+)\)/, '$1') + ' ' + (g.stones[t.tier] ?? 0)).join(' · ') + '  |  보호 주문서 ' + g.protects;
  }

  /** 이름 있는 유니크 줄: 효과·거래·lore (유니크가 아니면 아무것도 안 그린다) */
  private namedRows(parent: HTMLElement, c: GearCard): void {
    if (!c.named) return;
    for (const l of c.namedLines) { const e = el('div', 'color:' + NAMED_COLOR + ';font-weight:900', parent, l); e.className = 'af'; e.dataset.testid = 'gear-named-fx'; }
    if (c.trade) { const t = el('div', 'color:#ffd9a8', parent, c.trade); t.className = 'af'; t.dataset.testid = 'gear-named-trade'; }
    if (c.lore) { const l = el('div', 'color:#8f9bbd;font-size:11px;font-style:italic;font-weight:600', parent, c.lore); l.className = 'af'; l.dataset.testid = 'gear-named-lore'; }
  }

  private card(parent: HTMLElement, c: GearCard): HTMLElement {
    const box = el('div', '--gc:' + c.color, parent);
    box.className = 'dh-g-card';
    box.dataset.testid = 'gear-card';
    // 강화 상세 머리: 고른 장비의 큰 그림(+강화 배지) 옆에 이름·등급·능력치
    const head = el('div', 'display:flex;gap:10px;align-items:center;', box);
    const big = gearThumb(head, c, 92);
    big.dataset.testid = 'gear-enhance-icon';
    const info = el('div', 'min-width:0;display:flex;flex-direction:column;gap:3px;', head);
    el('div', '', info, c.name).className = 'nm';
    el('div', '', info, c.gradeName + (c.named ? ' · 유니크' : '') + ' · ' + c.slotName + ' · 아이템 Lv ' + c.ilvl + (c.equipped ? ' · 장착 중' : '')).className = 'sub';
    for (const s of c.stats) el('div', '', info, s).className = 'st';
    for (const a of c.affixes) el('div', '', box, a).className = 'af';
    this.namedRows(box, c);
    if (c.unique) { const u = el('div', 'color:#f2b233;font-weight:900', box, c.unique); u.className = 'af'; u.dataset.testid = 'gear-unique'; }
    if (c.sockets) { const sk = el('div', 'color:#9ff0ff', box, c.sockets); sk.className = 'af'; sk.dataset.testid = 'gear-sockets'; }
    if (c.otherHero) el('div', '', box, '다른 영웅 전용 무기').className = 'dh-g-warn';
    return box;
  }

  /** 강화 재료 줄: 강화석 등급별 그림 + 수, 보호 주문서 그림 + 수 */
  private matsRow(parent: HTMLElement, view: GameView): void {
    const g = view.gear;
    const line = el('div', 'display:flex;flex-wrap:wrap;gap:6px;align-items:center;', parent);
    line.className = 'dh-g-mats';
    line.dataset.testid = 'gear-mats';
    const names = STONE_TIERS.map((t) => t.name.replace(/강화석\((.+)\)/, '$1'));
    STONE_TIERS.forEach((t, i) => matChip(line, 'ui/items/' + t.id + '.webp', '강화석(' + names[i] + ')', g.stones[t.tier] ?? 0, 36, 'gear-stone-' + t.id));
    matChip(line, 'ui/items/scroll_protect.webp', '보호 주문서', g.protects, 36, 'gear-protect-have');
  }

  /** 바깥(가방 왼쪽 장착 칸)에서 장비를 고른다 */
  select(uid: string | null): void { if (uid && this.sel !== uid) { this.sel = uid; this.version++; } }
  get selected(): string | null { return this.sel; }

  /**
   * 장비 아이콘 칸(가방 격자·장착 칸·대장간 목록 공용). 빈 칸은 부위 이름을 점선 안에 쓴다.
   * 장착 중이면 「E」, 강화 단계가 있으면 +N, 다른 영웅 전용 무기는 어둡게 덮는다
   */
  tile(parent: HTMLElement, c: GearCard | null, testid: string, o: { empty?: string; onPick?: (uid: string) => void } = {}): HTMLElement {
    const b = el(c ? 'button' : 'div', c ? '--gc:' + c.color : '', parent);
    b.className = 'dh-g-tile' + (c ? '' : ' empty') + (c && c.uid === this.sel ? ' sel' : '');
    b.dataset.testid = testid;
    if (!c) { el('span', '', b, o.empty ?? ''); return b; }
    (b as HTMLButtonElement).type = 'button';
    b.dataset.focusKey = 'gear:' + c.uid;
    b.dataset.uid = c.uid;
    if (c.grade === 'legend') b.classList.add('legend');
    else if (c.grade === 'set') b.classList.add('set');
    gearImg(b, c.icon, c.iconFallbacks);
    if (c.badge) badgeImg(b, c.badge);
    if (c.otherHero) el('i', '', b).className = 'ban';
    if (c.enh > 0) el('span', '', b, '+' + c.enh).className = 'enh';
    if (c.equipped) el('span', '', b, 'E').className = 'eq';
    // 방금 장착한 칸: 초록 번쩍임 + 「장착됨」 배지(CSS 애니메이션이 스스로 사라진다)
    if (equipMarks.recent(c.uid, performance.now())) { b.classList.add('just'); el('span', '', b, '✔장착됨').className = 'dh-eq-badge'; b.dataset.justEquipped = '1'; }
    b.setAttribute('aria-label', c.name + ' · ' + c.gradeName + ' ' + c.slotName + (c.enh > 0 ? ' +' + c.enh : ''));
    b.setAttribute('data-tip', gearTipText(c));
    b.setAttribute('aria-pressed', c.uid === this.sel ? 'true' : 'false');
    b.addEventListener('click', () => {
      // 이미 고른 장비(장착 칸의 「E」 칸 등)를 다시 눌러도 눌림이 보이게 짧게 강조한다: 상세 카드는 이미 그 장비라 화면이 안 바뀐다(RG-011)
      if (c.uid === this.sel) b.animate?.([{ transform: 'scale(.9)' }, { transform: 'scale(1.06)' }, { transform: 'scale(1)' }], { duration: 220, easing: 'ease-out' });
      this.pick(c.uid); o.onPick?.(c.uid);
    });
    return b;
  }

  /** 가방 창 「장비」 탭(장비 통합): 부위 거르기 + 가방 아이콘 격자 + 상세(능력치·옵션·지금 장착과 비교·장착/벗기/정리) */
  renderBag(body: HTMLElement, view: GameView, locked: boolean): void {
    const g = view.gear;
    const root = el('div', '', body);
    root.className = 'dh-g';
    root.dataset.testid = 'gear-bag';
    this.modeNotice(root, view);
    const hero = view.heroId ?? 'kael';
    const eq = equippedCards(g, hero);
    const bag = bagCards(g, hero);
    const all = [...eq.filter((c): c is GearCard => !!c), ...bag];
    if (this.sel && !all.some((x) => x.uid === this.sel)) this.sel = null;
    if (!this.sel) this.sel = eq.find((c) => c)?.uid ?? bag[0]?.uid ?? null;
    // 고른 장비 상세(위) — 가로 화면은 왼쪽 장착 칸, 세로 화면은 위 미니 줄의 장착 칸에서도 고른다
    const cur = this.sel ? all.find((c) => c.uid === this.sel) ?? null : null;
    if (cur) this.detail(root, view, cur, locked);
    // 세트 효과(장착 부위 수에 따라 켜짐·꺼짐)
    const sets = setRows(g);
    if (sets.length) {
      const sb = el('div', '', root);
      sb.className = 'dh-g-log';
      sb.dataset.testid = 'gear-sets';
      for (const st of sets) {
        el('div', 'color:#3fd07a;font-weight:900', sb, `${st.name} ${st.count}/4`);
        for (const ln of st.lines) el('div', 'color:' + (ln.on ? '#7dffa0' : '#7d86a8'), sb, (ln.on ? '● ' : '○ ') + ln.text);
      }
    }
    const h2 = el('div', '', root);
    h2.className = 'dh-g-h';
    el('span', '', h2, '가방');
    // 더 좋은 장비가 있으면 한 번에 모두 장착(부위마다 가장 좋은 하나)
    if (equipHints.count > 0 && !locked) {
      const all = this.btn(h2, '추천 장비 모두 장착 (' + equipHints.count + ')', () => { window.dispatchEvent(new CustomEvent(EQUIP_BEST_EVENT)); }, { primary: true, testid: 'gear-equip-all' });
      all.style.minHeight = '34px'; all.style.padding = '3px 10px'; all.style.fontSize = '12px';
    }
    el('small', '', h2, bag.length + ' / ' + BAG_CAP + (bag.length >= BAG_CAP ? ' · 가득 참: 새 장비는 바로 정리' : ''));
    const fl = el('div', '', root);
    fl.className = 'dh-g-filter';
    fl.dataset.testid = 'gear-filter';
    for (const f of ['all', ...SLOT_ORDER] as const) {
      const n = f === 'all' ? bag.length : bag.filter((c) => c.slot === f).length;
      const b = el('button', '', fl, (f === 'all' ? '전체' : SLOT_KO[f]) + (n ? ' ' + n : ''));
      (b as HTMLButtonElement).type = 'button';
      b.className = 'dh-g-chip';
      b.dataset.testid = 'gear-filter-' + f;
      b.setAttribute('aria-pressed', this.filter === f ? 'true' : 'false');
      b.addEventListener('click', () => { if (this.filter !== f) { this.filter = f; this.version++; } });
    }
    const grid = el('div', '', root);
    grid.className = 'dh-g-grid';
    grid.dataset.testid = 'gear-bag-grid';
    const shown = this.filter === 'all' ? bag : bag.filter((c) => c.slot === this.filter);
    if (bag.length === 0) el('div', 'grid-column:1/-1;font:700 12px ' + FONT + ';color:#aeb9da;', grid, '던전에서 몹을 쓰러뜨리면 바닥에 장비가 떨어진다. 빛기둥 위를 지나가면 줍는다. 마을 대장간·잡화상에서도 살 수 있다.');
    else if (shown.length === 0) el('div', 'grid-column:1/-1;font:700 12px ' + FONT + ';color:#aeb9da;', grid, '이 부위 장비가 가방에 없다.');
    for (const c of shown) this.tile(grid, c, 'gear-item');
    // 다른 영웅이 낀 무기는 가방에서 빼고 안내 줄만 둔다(정리·거래 불가)
    for (const r of otherHeroWornRows(g)) { const n = el('div', 'grid-column:1/-1;font:700 12px ' + FONT + ';color:#aeb9da;', grid, r.text); n.dataset.testid = 'gear-other-hero'; }
    this.mats(root, view);
  }

  /** 상세 카드: 큰 아이콘 · 이름·능력치·옵션 · 지금 장착한 같은 부위와 비교 · 장착/벗기/정리 */
  private detail(root: HTMLElement, view: GameView, cur: GearCard, locked: boolean): void {
    const g = view.gear;
    const box = el('div', '--gc:' + cur.color, root);
    box.className = 'dh-g-card';
    box.dataset.testid = 'gear-card';
    box.dataset.uid = cur.uid;
    if (equipMarks.recent(cur.uid, performance.now())) {
      box.classList.add('just');
      const chip = el('div', '', box, '✔ 장착됨 · 능력치에 반영됐어요'); chip.className = 'dh-eq-badge'; chip.dataset.testid = 'gear-equipped-badge';
    }
    const top = el('div', '', box);
    top.className = 'dh-g-detail';
    const pic = el('div', 'min-width:0;', top);
    const big = this.tile(pic, cur, 'gear-card-icon');
    // 칸 폭을 채운다(버튼은 내용 폭으로 줄어 아이콘이 점만 하게 보였다)
    big.style.width = '100%';
    big.style.cursor = 'default';
    const info = el('div', 'min-width:0;display:flex;flex-direction:column;gap:3px;', top);
    el('div', '', info, cur.name + (cur.enh > 0 ? ' +' + cur.enh : '')).className = 'nm';
    el('div', '', info, cur.gradeName + (cur.named ? ' · 유니크' : '') + ' · ' + cur.slotName + ' · 아이템 Lv ' + cur.ilvl + (cur.equipped ? ' · 장착 중' : '')).className = 'sub';
    for (const s2 of cur.stats) el('div', '', info, s2).className = 'st';
    for (const a of cur.affixes) el('div', '', info, a).className = 'af';
    this.namedRows(info, cur);
    if (cur.unique) { const u = el('div', 'color:#f2b233;font-weight:900', info, cur.unique); u.className = 'af'; u.dataset.testid = 'gear-unique'; }
    if (cur.sockets) { const sk = el('div', 'color:#9ff0ff', info, cur.sockets); sk.className = 'af'; sk.dataset.testid = 'gear-sockets'; }
    if (cur.otherHero) el('div', '', info, '다른 영웅 전용 무기').className = 'dh-g-warn';
    // 비교 열: 같은 부위에 지금 장착한 장비 → 고른 장비(아이템 능력치 차이)
    const cmp = el('div', '', top);
    cmp.className = 'dh-g-cmp';
    cmp.dataset.testid = 'gear-compare';
    const slot = cur.slot;
    const curUid = slot ? g.equipped[slot] : null;
    if (cur.equipped) el('span', '', cmp, '장착 중 · 능력치에 반영됨');
    else {
      const item = g.items.find((x) => x.uid === cur.uid)!;
      const wear = curUid ? g.items.find((x) => x.uid === curUid) ?? null : null;
      el('span', '', cmp, wear ? '장착한 ' + (slot ? SLOT_KO[slot] : '') + ' 대비' : '빈 ' + (slot ? SLOT_KO[slot] : '') + ' 칸에 장착하면');
      const rows = gearCompare(item, wear);
      if (rows.length === 0) el('span', '', cmp, '능력치 같음');
      for (const r of rows) {
        const d = el('div', '', cmp);
        d.dataset.testid = 'gear-delta-' + r.key;
        el('span', '', d, r.label);
        el('span', '', d, r.from + ' → ' + r.to);
        el('b', '', d, r.up ? '▲' : '▼').className = r.up ? 'up' : 'dn';
      }
    }
    const act = el('div', '', box);
    act.className = 'dh-g-row';
    if (cur.equipped && slot) {
      this.btn(act, '벗기', () => this.send({ type: 'gearUnequip', slot, requestId: makeRequestId() }), { disabled: locked, testid: 'gear-unequip' });
    } else {
      this.btn(act, curUid ? '바꿔 장착' : '장착하기', () => this.send({ type: 'gearEquip', uid: cur.uid, requestId: makeRequestId() }), { primary: true, disabled: locked || cur.otherHero, testid: 'gear-equip' });
      this.salvage.sync(cur.uid);
      if (this.salvage.armed === cur.uid) {
        // 세트·전설: 화면 안 두 번째 단계(confirm() 없이)
        const ask = el('div', '', box);
        ask.className = 'dh-g-row';
        ask.dataset.testid = 'gear-salvage-ask';
        el('span', 'font:800 13px ' + FONT + ';color:#ffe7a0;', ask, `${cur.gradeName} 장비입니다. 정말 정리할까요? (${cur.salvage})`);
        this.btn(ask, '네, 정리', () => {
          if (this.salvage.confirm(cur.uid)) this.send({ type: 'gearSalvage', uid: cur.uid, requestId: makeRequestId() });
          this.version++;
        }, { primary: true, disabled: locked, testid: 'gear-salvage-confirm' });
        this.btn(ask, '취소', () => { this.salvage.cancel(); this.version++; }, { testid: 'gear-salvage-cancel' });
      } else {
        this.btn(act, '정리 (' + cur.salvage + ')', () => {
          if (this.salvage.press(cur.uid, cur.grade) === 'send') this.send({ type: 'gearSalvage', uid: cur.uid, requestId: makeRequestId() });
          this.version++;
        }, { disabled: locked, testid: 'gear-salvage' });
      }
    }
    el('div', '', box, '강화·보석은 마을 대장간에서 한다.').className = 'dh-g-note';
  }

  /** 대장간 창 「강화」 탭: 장비 목록 + 강화 카드(확률·천장 게이지·비용·보호 주문서) + 강화 장부 + 보호 주문서 상점 */
  renderForge(body: HTMLElement, view: GameView, gold: number, town: number, locked: boolean): void {
    const g = view.gear;
    const root = el('div', '', body);
    root.className = 'dh-g';
    root.dataset.testid = 'gear-forge';
    this.modeNotice(root, view);
    const hero = view.heroId ?? 'kael';
    const all = [...equippedCards(g, hero).filter((c): c is GearCard => !!c), ...bagCards(g, hero)];
    if (this.sel && !all.some((c) => c.uid === this.sel)) this.sel = null;
    if (!this.sel) this.sel = all[0]?.uid ?? null;
    this.matsRow(root, view);
    if (all.length === 0) { el('div', 'font:700 13px ' + FONT + ';color:#aeb9da;', root, '강화할 장비가 없다. 던전에서 드롭 장비를 주워 오라.'); return; }
    // C4: 일반 강화(+1~+15)와 초월 강화(+16~+20) 전환. 초월 구역은 숙련 보기가 있을 때만
    this.masteryPanel.bodyW = this.bodyW;
    if (view.mastery) {
      this.masteryPanel.renderModeToggle(root, view, () => { this.version++; });
      if (this.masteryPanel.forgeMode === 'transcend') { this.masteryPanel.renderTranscend(root, view, locked); return; }
    }
    const lay = forgeSplit(this.bodyW, this.narrow);
    const split = el('div', '', root);
    split.className = 'dh-g-split';
    split.style.gridTemplateColumns = lay.stacked ? '1fr' : lay.leftW + 'px minmax(0,1fr)';
    // 장비 격자: 정사각 칸을 정수 px로 정해 그림이 잘리지 않게(칸 높이가 줄어 아이콘 조각처럼 보이던 것을 고침)
    const tg = scrollTileGrid(all.length, lay.leftW - 6, 4, 56, 6, 6, 84);
    const list = el('div', '', split);
    list.className = 'dh-g-list';
    list.dataset.testid = 'gear-forge-grid';
    list.style.cssText = `display:grid;grid-template-columns:repeat(${tg.cols},${tg.tile}px);grid-auto-rows:${tg.tile}px;gap:${tg.gap}px;box-sizing:border-box;max-height:${tg.maxHeight}px;overflow-y:auto;overflow-x:hidden;align-content:start;justify-content:start;padding:2px;` + (tg.scroll ? 'scrollbar-width:thin;scrollbar-gutter:stable;' : '');
    for (const c of all) {
      const t = this.tile(list, c, 'gear-forge-item');
      t.style.width = tg.tile + 'px'; t.style.height = tg.tile + 'px';
    }
    const right = el('div', 'display:flex;flex-direction:column;gap:8px;min-width:0;', split);
    const m = this.sel ? enhanceModel(g, this.sel, gold) : null;
    if (m) {
      const cur = all.find((c) => c.uid === m.uid)!;
      const box = this.card(right, cur);
      box.dataset.testid = 'gear-enhance-card';
      if (m.max) el('div', '', box, '최대 강화(+15)').className = 'dh-g-warn';
      else {
        el('div', '', box, `+${m.enh} → +${m.to}   ${m.statFrom} → ${m.statTo}`).className = 'st';
        const ch = el('div', '', box, m.chance);
        ch.className = 'dh-g-chance';
        ch.dataset.testid = 'gear-chance';
        el('div', '', box, `기본 ${m.base} × 천장 ${m.pityMult}`).className = 'dh-g-note';
        const gauge = el('div', '', box);
        gauge.className = 'dh-g-gauge';
        gauge.dataset.testid = 'gear-pity';
        gauge.setAttribute('role', 'progressbar');
        gauge.setAttribute('aria-valuemin', '0'); gauge.setAttribute('aria-valuemax', '100'); gauge.setAttribute('aria-valuenow', String(Math.round(m.gauge * 100)));
        gauge.setAttribute('aria-label', '천장 게이지');
        el('span', 'width:' + Math.round(m.gauge * 100) + '%', gauge);
        el('div', '', box, `천장 게이지 ${Math.round(m.gauge * 100)}% · 이 단계 실패 ${m.fails}회`).className = 'dh-g-note';
        el('div', '', box, PITY_RULE).className = 'dh-g-note';
        el('div', '', box, m.failText).className = m.downRisk ? 'dh-g-warn' : 'dh-g-note';
        el('div', '', box, `${m.stoneName} ${m.stonesHave}/${m.stonesNeed} · 골드 ${gold.toLocaleString('ko-KR')}/${m.goldNeed.toLocaleString('ko-KR')}`).className = 'dh-g-mats';
        if (m.convText) el('div', '', box, m.convText).className = m.affordable ? 'dh-g-note' : 'dh-g-warn';
        if (m.downRisk) {
          const row = el('label', 'display:flex;gap:8px;align-items:center;font:800 13px ' + FONT + ';', box);
          const cb = el('input', 'width:20px;height:20px;', row) as HTMLInputElement;
          cb.type = 'checkbox';
          // 마지막 주문서를 쓰면(또는 잠기면) 선택을 풀어 다음 강화가 no_protect로 계속 막히지 않게 한다(PR #154 P2)
          this.protect = protectRequested(this.protect, m);
          cb.checked = this.protect;
          cb.disabled = !m.protectUnlocked || m.protects < 1;
          cb.dataset.testid = 'gear-protect';
          cb.addEventListener('change', () => { this.protect = cb.checked; this.version++; });
          el('span', '', row, m.protectUnlocked ? `보호 주문서 사용 (보유 ${m.protects}) — 실제로 하락할 때만 1장` : '보호 주문서: 지역6 클리어 뒤');
        }
        if (m.capText) el('div', '', box, m.capText).className = 'dh-g-warn';
        const act = el('div', '', box);
        act.className = 'dh-g-row';
        const go = this.btn(act, '강화  +' + m.enh + ' → +' + m.to, () => this.send({ type: 'gearEnhance', uid: m.uid, protect: protectRequested(this.protect, m), requestId: makeRequestId() }),
          { primary: true, disabled: locked || m.capped || !m.affordable, testid: 'gear-enhance' });
        Object.assign(go.style, { flex: '1 1 100%', minHeight: '52px', fontSize: '17px' });
      }
    }
    const h = el('div', '', right);
    h.className = 'dh-g-h';
    el('span', '', h, '강화 장부');
    el('small', '', h, '최근 8회');
    const log = el('div', '', right);
    log.className = 'dh-g-log';
    log.dataset.testid = 'gear-log';
    const rows = logRows(g.log, g.items);
    if (rows.length === 0) el('div', 'color:#aeb9da', log, '아직 기록이 없다.');
    for (const r of rows) el('div', 'color:' + TONE[r.tone], log, r.text);
    const shop = el('div', '', right);
    shop.className = 'dh-g-row';
    matChip(shop, 'ui/items/scroll_protect.webp', '보호 주문서', g.protects, 40);
    el('span', 'flex:1 1 140px;min-width:120px;font:700 12px ' + FONT + ';color:#cfe0ff;', shop, protectShopText(town, g.protectUnlocked));
    this.btn(shop, '구매', () => this.send({ type: 'gearBuyProtect', requestId: makeRequestId() }), { disabled: locked || !g.protectUnlocked, testid: 'gear-buy-protect' });
  }
}
